import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAssignedAssessments } from "../redux/slices/formSlice";
import { addTask, addTaskAdmin } from "../redux/slices/attendanceSlice";
import { getMyFacilities, unassignAssessment } from "../redux/slices/facilitySlice";
import Toast from "../components/Toast";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";

const FacilityDetailDashboardData = ({ rows }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Date filter hook
  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  // Facility state - simplified
  const [selectedFacilityId, setSelectedFacilityId] = useState("all");

  const [facilitySearch, setFacilitySearch] = useState("");
  const [showFacilityDropdown, setShowFacilityDropdown] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rows || 10);

  // Toast states
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });
  const [initialLoad, setInitialLoad] = useState(true);

  const buttonRefs = useRef([]);

  // Redux state
  const {
    assignedAssessments,
    isAssignedAssessmentsLoading,
    assignedAssessmentsError,
  } = useSelector((state) => state.form);

  const { 
    isUnassigningAssessment, 
    unassignAssessmentError 
  } = useSelector((state) => state.facility);

  const { isAddingTask, addTaskError, successMessage } = useSelector(
    (state) => state.attendance,
  );

  const {
    myFacilities = { data: [] },
    isLoading: isLoadingFacilities = false,
  } = useSelector((state) => state.facility || {});

  // Build facilities list from API
  const [facilities, setFacilities] = useState([]);

  // Get user role and user ID
  useEffect(() => {
    const role = localStorage.getItem("role");
    const userData = localStorage.getItem("user");
    setUserRole(role || "guest");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setLoggedInUserId(parsedUser.id);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const isCustomer = userRole === "customer";
  const isAdmin = userRole === "admin";

  // Fetch facilities on mount
  useEffect(() => {
    dispatch(getMyFacilities({ page: 1, per_page: 10000 }));
  }, [dispatch]); 

  useEffect(() => {
    if (myFacilities.data && myFacilities.data.length > 0) {
      const facilityList = myFacilities.data
        .map((facility) => {
          // Check if this is admin data (has direct facility_name) or team/customer data (has facility_id)
          if (facility.facility_name && !facility.facility_id) {
            // Admin format
            return {
              id: facility.id,
              name: facility.facility_name,
            };
          } else if (facility.facility_id) {
            // Team or Customer format - use facility_id
            return {
              id: facility.facility_id, // Use facility_id instead of id
              name: facility.facility_name,
            };
          }
          // Fallback
          return {
            id: facility.id || facility.facility_id,
            name: facility.facility_name || "Unknown Facility",
          };
        })
        .filter((f) => f.id && f.name) // Remove any invalid entries
        .sort((a, b) => a.name.localeCompare(b.name));
      setFacilities(facilityList);
    } else {
    }
  }, [myFacilities, userRole]);

  // Function to fetch assessments
  const fetchAssessments = (page = 1, perPage = rowsPerPage) => {
    const dateRange = getDateRange();
    const params = { page, per_page: rowsPerPage };

    if (selectedFacilityId !== "all") {
      // The selectedFacilityId should already be the correct ID from our mapping
      params.facility_id = parseInt(selectedFacilityId, 10);
    }

    if (dateRange.from_date) {
      params.from_date = dateRange.from_date;
    }
    if (dateRange.to_date) {
      params.to_date = dateRange.to_date;
    }

    dispatch(getAssignedAssessments(params));
  };
  

  // useEffect with all dependencies
  useEffect(() => {
    setCurrentPage(1);
    fetchAssessments(1);
  }, [
    selectedFacilityId,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
  ]);

  // Mark initial load complete
  useEffect(() => {
    if (!isAssignedAssessmentsLoading && initialLoad) {
      setInitialLoad(false);
    }
  }, [isAssignedAssessmentsLoading, initialLoad]);

  // Toast for add task
  useEffect(() => {
    if (successMessage && successMessage.includes("Task")) {
      showToast(successMessage, "success");
    }
    if (addTaskError) {
      showToast(addTaskError, "error");
    }
  }, [successMessage, addTaskError]);

  const handleFacilityChange = (facilityId, facilityName = "") => {
    setSelectedFacilityId(facilityId);

    if (facilityId === "all") {
      setFacilitySearch("");
    } else {
      setFacilitySearch(facilityName);
    }
  };

  const handleDropdownToggle = (index, e) => {
    e.stopPropagation();
    if (buttonRefs.current[index]) {
      const rect = buttonRefs.current[index].getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const handleEdit = (row) => {
    showToast(`Edit Employee: ${row.formName} successfully`, "success");
    setDropdownOpen(null);
  };

  const handleDelete = (row) => {
    // Add a check to make sure row exists
    if (!row || !row.originalData) {
      showToast("Unable to delete: Data not found", "error");
      return;
    }
    
    // Get the assignment ID from the original data
    const assignmentId = row.originalData?.id;
    if (!assignmentId) {
      showToast("No assignment ID found", "error");
      return;
    }
    
    // Confirm delete
    if (window.confirm(`Are you sure you want to unassign this assessment?`)) {
      dispatch(unassignAssessment(assignmentId)).then((result) => {
        if (result.payload?.success) {
          showToast("Assessment unassigned successfully!", "success");
          setDropdownOpen(null);
          // Refresh the data
          fetchAssessments(currentPage);
        } else {
          showToast(result.payload?.message || "Failed to unassign assessment", "error");
        }
      });
    }
  };

  const handleAddTask = (form) => {
    setSelectedForm(form);
    setShowAddTaskModal(true);
  };

  const handleSaveTask = (taskData) => {
    if (isAdmin) {
      const apiTaskData = {
        title: taskData.title,
        description: taskData.description || "",
        startDateTime: taskData.startDateTime,
        endDateTime: taskData.endDateTime,
        assigned_assessment_id: taskData.assigned_assessment_id,
        facility_id: selectedForm?.originalData?.facility_id,
        user_id: selectedForm?.originalData?.user_id,
      };

      dispatch(addTaskAdmin(apiTaskData)).then((action) => {
        if (action.payload?.success) {
          showToast("Task added to timelogs successfully!", "success");
          setShowAddTaskModal(false);
          setSelectedForm(null);
          fetchAssessments();
        }
      });
    } else {
      const apiTaskData = {
        title: taskData.title,
        description: taskData.description || "",
        startDateTime: taskData.startDateTime,
        endDateTime: taskData.endDateTime,
        assigned_assessment_id: taskData.assigned_assessment_id || 1,
      };

      dispatch(addTask(apiTaskData)).then((action) => {
        if (action.payload?.success) {
          showToast("Task added to timelogs successfully!", "success");
          setShowAddTaskModal(false);
          setSelectedForm(null);
          fetchAssessments();
        }
      });
    }
  };

  

  // Click outside handler for action dropdown only
  useEffect(() => {
    const handleClickOutside = (event) => {
      setDropdownOpen(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
  if (selectedFacilityId === "all") {
    setFacilitySearch("");
  }
  }, []);

  const showToast = (message, type = "info") => {
    if (
      initialLoad &&
      (message.includes("Operation successful") ||
        message.includes("successfully"))
    ) {
      return;
    }
    setToast({ show: true, message, type });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000);
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  const getFormStatusInfo = (assessment) => {
    const submittedAssessmentId = assessment.submitted_assessment_id;
    const startTime = assessment.start_time;
    const endTime = assessment.end_time;

    const isAssignedToCurrentCustomer =
      isCustomer && assessment.user_id === loggedInUserId;
    const isFormCompleted = submittedAssessmentId !== null;

    if (isCustomer && !isFormCompleted && !isAssignedToCurrentCustomer) {
      return {
        isCompleted: false,
        canShowTaskButton: false,
        taskButtonText: "Form In-Process",
        taskButtonDisabled: true,
        taskButtonStyle: {
          background: "#cccccc",
          cursor: "not-allowed",
          opacity: 0.6,
        },
        fillFormText: null,
        fillFormIcon: null,
        showAssignedToText: true,
        assignedToName: assessment.user_name || "Employee",
      };
    }

    if (isAdmin && !isFormCompleted && !isAssignedToCurrentCustomer) {
      return {
        isCompleted: false,
        canShowTaskButton: false,
        taskButtonText: "Form In-Process",
        taskButtonDisabled: true,
        taskButtonStyle: {
          background: "#cccccc",
          cursor: "not-allowed",
          opacity: 0.6,
        },
        fillFormText: null,
        fillFormIcon: null,
        showAssignedToText: true,
        assignedToName: assessment.user_name || "Employee",
      };
    }

    if (submittedAssessmentId === null) {
      return {
        isCompleted: false,
        canShowTaskButton: false,
        taskButtonText: "Form In-Process",
        taskButtonDisabled: true,
        taskButtonStyle: {
          background: "#cccccc",
          cursor: "not-allowed",
          opacity: 0.6,
        },
        fillFormText: "Fill",
        fillFormIcon: "material-symbols:edit-document",
        showAssignedToText: false,
      };
    } else if (startTime === null && endTime === null) {
      return {
        isCompleted: true,
        canShowTaskButton: !isCustomer,
        taskButtonText: "Add Task",
        taskButtonDisabled: isCustomer,
        taskButtonStyle: {
          background: "#2196F3",
          cursor: isCustomer ? "not-allowed" : "pointer",
        },
        fillFormText: "View",
        fillFormIcon: "ic:baseline-remove-red-eye",
        showAssignedToText: false,
      };
    } else {
      return {
        isCompleted: true,
        canShowTaskButton: !isCustomer,
        taskButtonText: "Task Added",
        taskButtonDisabled: true,
        taskButtonStyle: {
          background: "#28a745",
          cursor: "not-allowed",
          opacity: 0.8,
        },
        fillFormText: "View",
        fillFormIcon: "ic:baseline-remove-red-eye",
        showAssignedToText: false,
      };
    }
  };

  const transformAssessmentData = () => {
    if (!assignedAssessments.data || assignedAssessments.data.length === 0) {
      return [];
    }

    return assignedAssessments.data.map((assessment) => {
      const formStatusInfo = getFormStatusInfo(assessment);
      const startTime = assessment.start_time
        ? new Date(assessment.start_time)
        : new Date();
      const endTime = assessment.end_time
        ? new Date(assessment.end_time)
        : new Date();

      const dateTimeDisplay = assessment.assessment_submitted_at ? (
        (() => {
          const [datePart, timePart] =
            assessment.assessment_submitted_at.split(" ");
          const [year, month, day] = datePart.split("-");
          const [hours, minutes, seconds = "00"] = timePart.split(":");
          const utcDate = new Date(
            Date.UTC(year, month - 1, day, hours, minutes, seconds),
          );

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: "1.3",
              }}>
              <span style={{ fontWeight: 500, color: "#333" }}>
                {utcDate.toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </span>
              <span style={{ fontSize: "0.8rem", color: "#666" }}>
                {utcDate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </span>
            </div>
          );
        })()
      ) : assessment.created_at ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.3",
          }}>
          <span style={{ fontWeight: 500, color: "#333" }}>
            {new Date(assessment.created_at).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })}
          </span>
          <span style={{ fontSize: "0.8rem", color: "#666" }}>
            {new Date(assessment.created_at).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </span>
        </div>
      ) : (
        "N/A"
      );

      let hoursWorked = "N/A";
      if (assessment.start_time && assessment.end_time) {
        const diffMs = endTime - startTime;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        hoursWorked = diffHours.toString();
      }

      const formName = assessment.category_name || "Assessment";
      const facility = assessment.facility_name || "Unknown Facility";

      return {
        id: assessment.id,
        formName,
        dateTimeDisplay,
        hoursWorked,
        customergroup: assessment.customer_group_name || "N/A",
        formStatus: formStatusInfo.isCompleted,
        facility,
        canShowTaskButton: formStatusInfo.canShowTaskButton,
        taskButtonText: formStatusInfo.taskButtonText,
        taskButtonDisabled: formStatusInfo.taskButtonDisabled,
        taskButtonStyle: formStatusInfo.taskButtonStyle,
        fillFormText: formStatusInfo.fillFormText,
        fillFormIcon: formStatusInfo.fillFormIcon,
        showAssignedToText: formStatusInfo.showAssignedToText || false,
        assignedToName: formStatusInfo.assignedToName,
        assessmentScore: assessment.assessment_score || 0,
        assessmentMaxScore: assessment.assessment_max_score || 0,
        scorePercentage:
          assessment.assessment_max_score > 0
            ? Math.round(
                (assessment.assessment_score /
                  assessment.assessment_max_score) *
                  100,
              )
            : 0,
        originalData: assessment,
      };
    });
  };

  const getScoreColor = (percentage) =>
    percentage < 80 ? "#dc3545" : "#28a745";

  const transformedData = transformAssessmentData();

  const filteredFacilities = facilities.filter((facility) =>
  facility.name
    .toLowerCase()
    .includes(facilitySearch.toLowerCase())
  );

  const employeeColumns = [
    { name: "facility", label: "Facility Name", options: { sort: false } },
    { name: "formName", label: "Task Name", options: { sort: false } },
    {
      name: "scorePercentage",
      label: "Score",
      options: {
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const row = transformedData[rowIndex];
          const score = row.assessmentScore;
          const maxScore = row.assessmentMaxScore;
          const percentage = row.scorePercentage;
          const scoreColor = getScoreColor(percentage);
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  fontWeight: 600,
                  color: scoreColor,
                  fontSize: "1rem",
                }}>
                {percentage}%
              </span>
              <span
                style={{ fontSize: "0.7rem", color: "#666", marginTop: "2px" }}>
                ({score} / {maxScore})
              </span>
            </div>
          );
        },
      },
    },
    { name: "customergroup", label: "Group Name", options: { sort: false } },
    {
      name: "dateTimeDisplay",
      label: "Date & Time",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => value,
      },
    },
    {
      name: "hoursWorked",
      label: "Hours Worked",
      options: {
        sort: false,
        customBodyRender: (value) => (
          <span
            style={{
              color: value === "N/A" ? "#999999" : "#000000",
              fontWeight: value === "N/A" ? 400 : 500,
            }}>
            {value === "N/A" ? "N/A" : `${value} hours`}
          </span>
        ),
      },
    },
    {
      name: "formStatus",
      label: "Status",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <div
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: value ? "#28a745" : "#dc3545",
                boxShadow: "0 0 5px rgba(0,0,0,0.2)",
                display: "inline-block",
              }}
              title={value ? "Completed" : "In-Process"}
            />
          </div>
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = transformedData[dataIndex];
          const handleActionClick = () => {
            const isCompleted = row.formStatus;
            let assessmentId = isCompleted
              ? row.originalData?.submitted_assessment_id
              : row.originalData?.id;
            if (!assessmentId) {
              showToast("Cannot navigate: No assessment ID found", "error");
              return;
            }
            const categoryId = row.originalData?.category_id;
            const categoryName =
              row.originalData?.category_name || "Assessment";
            const cleanRow = {
              id: row.id,
              formName: row.formName,
              hoursWorked: row.hoursWorked,
              customergroup: row.customergroup,
              formStatus: row.formStatus,
              facility: row.facility,
              canShowTaskButton: row.canShowTaskButton,
              taskButtonText: row.taskButtonText,
              taskButtonDisabled: row.taskButtonDisabled,
              fillFormText: row.fillFormText,
              fillFormIcon: row.fillFormIcon,
              assessmentScore: row.assessmentScore,
              assessmentMaxScore: row.assessmentMaxScore,
              scorePercentage: row.scorePercentage,
            };
            if (isCompleted) {
              navigate(`/view-form/${assessmentId}`, {
                state: {
                  form: cleanRow,
                  assessmentId,
                  category_id: categoryId,
                  category_name: categoryName,
                  isCompleted: true,
                },
              });
            } else {
              navigate(`/fill-form/${assessmentId}`, {
                state: {
                  form: cleanRow,
                  assessmentId,
                  category_id: categoryId,
                  category_name: categoryName,
                  isCompleted: false,
                },
              });
            }
          };

          let tooltipMessage = "Add task to timelogs";
          if (row.taskButtonText === "Task Already Added")
            tooltipMessage = "Task has already been added to timelogs";
          else if (row.taskButtonText === "Form In-Process")
            tooltipMessage = "Cannot add task for forms in process"; 

          // Delete button component to reuse
          const deleteButton = isAdmin && (
            <button
              onClick={() => handleDelete(row)}
              style={{
                background: "#dc3545",
                border: "none",
                cursor: "pointer",
                padding: "5px 12px",
                color: "white",
                borderRadius: "7px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              title="Unassign assessment">
              <Icon icon="material-symbols:delete-outline" width="17" height="20" />
              Delete
            </button>
          );

          if (row.showAssignedToText) {
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    color: "#666",
                    fontSize: "13px",
                    fontStyle: "italic",
                    backgroundColor: "#f5f5f5",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}>
                  <Icon icon="mdi:account" width="16" height="16" />
                  Assigned to {row.assignedToName}
                </span>
                {deleteButton}
              </div>
            );
          }

          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                onClick={handleActionClick}
                style={{
                  background: "#8B2885",
                  border: "none",
                  cursor: "pointer",
                  padding: "5px 12px",
                  color: "white",
                  borderRadius: "7px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}>
                <Icon icon={row.fillFormIcon} width="17" height="20" />
                {row.fillFormText}
              </button>
              {deleteButton}
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    rowsPerPageOptions: [5, 10, 15, 100],
    rowsPerPage,
    serverSide: true,
    page: (assignedAssessments.current_page || 1) - 1,
    count: assignedAssessments.total || 0,
    onChangePage: (page) => {
      const newPage = page + 1;

      setCurrentPage(newPage);

      fetchAssessments(newPage);
    },
    onChangeRowsPerPage: (newRowsPerPage) => {
      setRowsPerPage(newRowsPerPage);
      fetchAssessments(1, newRowsPerPage);

      const dateRange = getDateRange();

      const params = {
        page: 1,
        per_page: newRowsPerPage,
      };

      if (selectedFacilityId !== "all") {
        params.facility_id = parseInt(selectedFacilityId, 10);
      }

      if (dateRange.from_date) {
        params.from_date = dateRange.from_date;
      }

      if (dateRange.to_date) {
        params.to_date = dateRange.to_date;
      }

      dispatch(getAssignedAssessments(params));
    },
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    textLabels: {
      body: {
        noMatch: isAssignedAssessmentsLoading
          ? "Loading assessments..."
          : assignedAssessmentsError
            ? `Error: ${assignedAssessmentsError}`
            : "No assessments found",
      },
    },
  };

  const AddTaskModal = ({ form, onClose, onSave, isAdding, userRole }) => {
    const getCurrentDateTime = () => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    };
    const currentDateTime = getCurrentDateTime();
    const [taskData, setTaskData] = useState({
      title: form ? `${form.formName} - ${form.facility}` : "",
      description: "",
      startDateTime: currentDateTime,
      endDateTime: new Date(new Date().getTime() + 60 * 60 * 1000)
        .toISOString()
        .slice(0, 16),
      assigned_assessment_id: form?.originalData?.id || 1,
    });

    const handleStartDateTimeChange = (e) => {
      const selectedDateTime = e.target.value;
      if (selectedDateTime > currentDateTime) {
        alert(
          "Cannot select a future date. Please select today or a past date.",
        );
        return;
      }
      setTaskData({
        ...taskData,
        startDateTime: selectedDateTime,
        endDateTime:
          selectedDateTime > taskData.endDateTime
            ? selectedDateTime
            : taskData.endDateTime,
      });
    };

    const handleEndDateTimeChange = (e) => {
      const selectedDateTime = e.target.value;
      if (selectedDateTime > currentDateTime) {
        alert(
          "Cannot select a future date. Please select today or a past date.",
        );
        return;
      }
      setTaskData({ ...taskData, endDateTime: selectedDateTime });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      const startDate = new Date(taskData.startDateTime);
      const endDate = new Date(taskData.endDateTime);
      const now = new Date();
      if (startDate > now) {
        alert(
          "Start date & time cannot be in the future. Please select today or a past date.",
        );
        return;
      }
      if (endDate > now) {
        alert(
          "End date & time cannot be in the future. Please select today or a past date.",
        );
        return;
      }
      if (endDate < startDate) {
        alert("End date & time cannot be before start date & time");
        return;
      }
      onSave(taskData);
    };

    return createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
        }}
        onClick={onClose}>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
          onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}>
            <h3 style={{ margin: 0, color: "#8B2885" }}>
              Add Task to Timelogs
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#666",
              }}>
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}>
                Task Title
              </label>
              <input
                type="text"
                value={taskData.title}
                onChange={(e) =>
                  setTaskData({ ...taskData, title: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                }}
                required
                disabled={isAdding}
              />
            </div>

            {userRole === "admin" && form?.originalData?.user_name && (
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "500",
                  }}>
                  Assigned User
                </label>
                <div
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}>
                  <Icon
                    icon="mdi:account"
                    width="20"
                    height="20"
                    color="#666"
                  />
                  <span>{form.originalData.user_name}</span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}>
                Facility
              </label>
              <input
                type="text"
                value={form?.facility || "Unknown Facility"}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                }}
                disabled
                readOnly
              />
            </div>
            <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "500",
                  }}>
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={taskData.startDateTime}
                  onChange={handleStartDateTimeChange}
                  max={currentDateTime}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                  }}
                  required
                  disabled={isAdding}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "500",
                  }}>
                  End Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={taskData.endDateTime}
                  onChange={handleEndDateTimeChange}
                  max={currentDateTime}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                  }}
                  required
                  disabled={isAdding}
                />
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "500",
                }}>
                Description
              </label>
              <textarea
                value={taskData.description}
                onChange={(e) =>
                  setTaskData({ ...taskData, description: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  minHeight: "80px",
                  resize: "vertical",
                }}
                placeholder="Add task details..."
                disabled={isAdding}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "white",
                  cursor: "pointer",
                  color: "#666",
                }}
                disabled={isAdding}>
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background:
                    "linear-gradient(90deg,rgba(216, 81, 80, 1) 0%,rgba(87, 36, 103, 1) 100% )",
                  cursor: "pointer",
                  color: "white",
                  fontWeight: "500",
                  opacity: isAdding ? 0.7 : 1,
                }}
                disabled={isAdding}>
                {isAdding ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"></span>{" "}
                    Adding...
                  </>
                ) : (
                  "Add to Timelogs"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body,
    );
  };

  // SIMPLE WORKING FACILITY FILTER - Like DailyAdminAttendance
  const renderFacilityFilter = () => (
    <div className="mb-4 pb-2 border-bottom">
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div style={{ minWidth: "300px" }}>
          <DateFilter {...dateFilter} onFilterChange={updateFilter} size="sm" />
        </div>

        {/* Simple select dropdown - guaranteed to work */}
        <div
          className="position-relative"
          style={{ minWidth: "250px" }}
        >
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="All facilities..."
            value={facilitySearch}
            onChange={(e) => {
              setFacilitySearch(e.target.value);
              setShowFacilityDropdown(true);
            }}
            onFocus={() => setShowFacilityDropdown(true)}
            onBlur={() =>
              setTimeout(() => setShowFacilityDropdown(false), 200)
            }
          />

          {showFacilityDropdown && (
            <div
              className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
              style={{
                zIndex: 1000,
                maxHeight: "250px",
                overflowY: "auto",
              }}
            >
              <div
                className="px-3 py-2"
                style={{ cursor: "pointer" }}
                onMouseDown={() =>
                  handleFacilityChange("all", "All Facilities")
                }
              >
                All Facilities
              </div>

              {filteredFacilities.map((facility) => (
                <div
                  key={facility.id}
                  className="px-3 py-2"
                  style={{ cursor: "pointer" }}
                  onMouseDown={() =>
                    handleFacilityChange(
                      String(facility.id),
                      facility.name
                    )
                  }
                >
                  {facility.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
        <span className="badge bg-light">
          <i className="fas fa-calendar-alt me-1"></i>
          {getDateRange().label}
        </span>
        <span className="badge bg-light">
          <i className="fas fa-building me-1"></i>
          {selectedFacilityId === "all"
            ? "All Facilities"
            : facilities.find((f) => f.id === parseInt(selectedFacilityId))
                ?.name}
        </span>
        {assignedAssessments?.total > 0 && (
          <span className="badge bg-success">
            <i className="fas fa-file-alt me-1"></i>
            {assignedAssessments.total} Assessments
          </span>
        )}
      </div>
    </div>
  );

  if (isAssignedAssessmentsLoading && !transformedData.length) {
    return (
      <>
        {renderFacilityFilter()}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading assessments...</span>
          </div>
        </div>
      </>
    );
  }

  if (assignedAssessmentsError && !transformedData.length) {
    return (
      <>
        {renderFacilityFilter()}
        <div className="alert alert-danger" role="alert">
          Error loading assessments: {assignedAssessmentsError}
          <button
            className="btn btn-sm btn-outline-danger ms-3"
            onClick={() => fetchAssessments()}>
            Retry
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={closeToast}
        duration={3000}
      />
      {renderFacilityFilter()}
      {transformedData.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No assessments found
          {selectedFacilityId !== "all" ? " for the selected facility" : ""}.
        </div>
      ) : (
        <div>
          <MUIDataTable
            data={transformedData}
            columns={employeeColumns}
            options={options}
            className="overflow-hidden packageTable"
          />
        </div>
      )}
      {dropdownOpen !== null &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: buttonPosition.top,
              left: buttonPosition.left - 100,
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              minWidth: "120px",
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() => handleEdit(transformedData[dropdownOpen])}>
              <Icon icon="line-md:edit" width="16" height="16" /> Edit
            </div>
            <div
              style={{
                padding: "8px 12px",
                color: "#D32F2F",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderTop: "1px solid #eee",
              }}
              onClick={() => handleDelete(transformedData[dropdownOpen])}>
              <Icon
                icon="material-symbols:delete-outline"
                width="16"
                height="16"
                color="#D32F2F"
              />{" "}
              Delete
            </div>
          </div>,
          document.body,
        )}
      {showAddTaskModal && (
        <AddTaskModal
          form={selectedForm}
          onClose={() => {
            setShowAddTaskModal(false);
            setSelectedForm(null);
          }}
          onSave={handleSaveTask}
          isAdding={isAddingTask}
          userRole={userRole}
        />
      )}
    </>
  );
};

export default FacilityDetailDashboardData;